<template>
  <div class="uuid-generator-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">UUID Generator (GUID)</h1>
            <p class="hero-subtitle">
              Generate unique UUIDs (Universally Unique Identifiers) for your development needs. 
              Click to copy any UUID to your clipboard instantly.
            </p>
            <div class="uuid-guid-note">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span><strong>Note:</strong> UUID is the official standard name. Microsoft calls them GUIDs - they're the same thing!</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Generator Section -->
    <section class="generator-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <!-- Generate Button -->
            <div class="text-center mb-5">
              <!-- UUID Type Selection -->
              <div class="uuid-type-selector mb-4">
                <label class="type-option">
                  <input v-model="uuidType" type="radio" name="uuidType" value="v4" class="type-radio">
                  <div class="type-card">
                    <div class="type-header">
                      <strong>UUID v4</strong>
                      <span class="type-badge">Random</span>
                    </div>
                    <p>Randomly generated, most common</p>
                  </div>
                </label>
                <label class="type-option">
                  <input v-model="uuidType" type="radio" name="uuidType" value="v7" class="type-radio">
                  <div class="type-card">
                    <div class="type-header">
                      <strong>UUID v7</strong>
                      <span class="type-badge type-badge-sortable">Sortable</span>
                    </div>
                    <p>Time-based, lexicographically sortable</p>
                  </div>
                </label>
              </div>

              <button class="btn-generate" @click="generateUuid">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Generate New UUID
              </button>
              <div class="mt-3">
                <label class="format-label">
                  <input v-model="uppercase" type="checkbox" class="format-checkbox">
                  Uppercase
                </label>
                <label class="format-label ms-3">
                  <input v-model="includeDashes" type="checkbox" class="format-checkbox">
                  Include Dashes
                </label>
              </div>
            </div>

            <!-- Generated UUIDs List -->
            <div class="uuids-container">
              <div v-if="uuids.length === 0" class="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                <p>No UUIDs generated yet</p>
                <p class="text-muted">Click the button above to generate your first UUID</p>
              </div>

              <TransitionGroup name="uuid-list" tag="div">
                <div
                  v-for="uuid in uuids"
                  :key="uuid.id"
                  :class="{ 'copied': uuid.copied }"
                  class="uuid-item"
                  @click="copyToClipboard(uuid)"
                >
                  <div class="uuid-content">
                    <div class="uuid-type-indicator">
                      <span class="version-badge" :class="{ 'sortable': uuid.type === 'v7' }">
                        {{ uuid.type === 'v7' ? 'v7' : 'v4' }}
                      </span>
                    </div>
                    <code class="uuid-value">{{ formatUuid(uuid.value) }}</code>
                    <span v-if="uuid.copied" class="copied-badge">Copied!</span>
                  </div>
                  <div class="uuid-actions">
                    <button class="btn-icon" title="Copy to clipboard" @click.stop="copyToClipboard(uuid)">
                      <svg v-if="!uuid.copied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                    <button class="btn-icon btn-delete" title="Remove" @click.stop="removeUuid(uuid.id)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </TransitionGroup>
            </div>

            <!-- Bulk Actions -->
            <div v-if="uuids.length > 0" class="bulk-actions">
              <button class="btn-bulk" @click="copyAllUuids">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy All ({{ uuids.length }})
              </button>
              <button class="btn-bulk btn-danger" @click="clearAllUuids">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <div class="info-card">
              <h3>About UUIDs</h3>
              <p>
                A UUID (Universally Unique Identifier) is a 128-bit number defined in RFC 4122, used to uniquely 
                identify information in computer systems. The probability of generating duplicate identifiers 
                is so small that it can be ignored for practical purposes.
              </p>
              
              <h4>UUID vs GUID - What's the Difference?</h4>
              <p>
                <strong>They're the same thing!</strong> Both refer to the exact same 128-bit identifier standard.
              </p>
              <ul>
                <li><strong>UUID (Universally Unique Identifier)</strong> - The official standard name defined in RFC 4122, used by most of the industry</li>
                <li><strong>GUID (Globally Unique Identifier)</strong> - Microsoft's terminology for the same concept, commonly used in .NET, SQL Server, and Windows development</li>
              </ul>
              <p>
                The format, structure, and generation algorithms are identical. The only difference is the name. 
                This tool generates standard UUID v4 identifiers, which work perfectly whether you call them UUIDs or GUIDs.
              </p>
              
              <h4>Common Use Cases</h4>
              <ul>
                <li>Database primary keys and unique identifiers</li>
                <li>API request IDs and correlation tracking</li>
                <li>Session identifiers and authentication tokens</li>
                <li>File and resource identifiers</li>
                <li>Distributed system unique identifiers</li>
                <li>Message queue and event identifiers</li>
                <li>Entity IDs in microservices architectures</li>
              </ul>
              
              <h4>UUID Versions</h4>
              <p>This tool supports two UUID versions:</p>
              <ul>
                <li>
                  <strong>UUID v4 (Random)</strong> - Randomly generated using cryptographic randomness. 
                  Most commonly used for general-purpose unique identifiers. 
                  <br>Example: <code>550e8400-e29b-41d4-a716-446655440000</code>
                </li>
                <li>
                  <strong>UUID v7 (Sortable)</strong> - Time-based and lexicographically sortable. 
                  Perfect for database primary keys, event IDs, and distributed systems where chronological ordering matters.
                  Includes a Unix timestamp in the first 48 bits, making them naturally sortable by creation time.
                  <br>Example: <code>018e3c5e-1234-7abc-9def-0123456789ab</code>
                </li>
              </ul>
              
              <h4>UUID Format</h4>
              <p>
                Standard format: <code>xxxxxxxx-xxxx-Vxxx-yxxx-xxxxxxxxxxxx</code>
                <br>
                Where 'V' indicates the version (4 or 7)
              </p>
              
              <h4>Why Use Sortable UUIDs (v7)?</h4>
              <ul>
                <li><strong>Database Performance</strong> - Better index performance in databases compared to random UUIDs</li>
                <li><strong>Time-ordered</strong> - Natural chronological sorting without additional timestamp columns</li>
                <li><strong>Distributed Systems</strong> - Maintain order across multiple servers generating IDs</li>
                <li><strong>Debugging</strong> - Easy to identify when records were created just by looking at the UUID</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

useHead({
  title: 'UUID Generator (GUID) | Developer Tools',
  meta: [
    { name: 'description', content: 'Generate unique UUIDs (Universally Unique Identifiers) for your development needs. UUID is the official standard name - Microsoft calls them GUIDs. Easy copy-to-clipboard functionality.' }
  ]
})

interface Uuid {
  id: number
  value: string
  copied: boolean
  type: 'v4' | 'v7'
}

const uuids = ref<Uuid[]>([])
const uppercase = ref(false)
const includeDashes = ref(true)
const uuidType = ref<'v4' | 'v7'>('v4')
let uuidCounter = 0

const generateUuidV4 = (): string => {
  // Generate a random UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

const generateUuidV7 = (): string => {
  // Generate a UUID v7 (time-ordered)
  const timestamp = Date.now()
  const randomBytes = new Uint8Array(10)
  crypto.getRandomValues(randomBytes)
  
  // Convert timestamp to hex (48 bits)
  const timestampHex = timestamp.toString(16).padStart(12, '0')
  
  // Version and variant bits
  const version = '7'
  const variant = ((randomBytes[0] & 0x3f) | 0x80).toString(16).padStart(2, '0')
  
  // Build UUID v7
  const uuid = [
    timestampHex.slice(0, 8),
    timestampHex.slice(8, 12),
    version + Array.from(randomBytes.slice(0, 2)).map(b => b.toString(16).padStart(2, '0')).join('').slice(1, 4),
    variant + Array.from(randomBytes.slice(2, 4)).map(b => b.toString(16).padStart(2, '0')).join('').slice(2),
    Array.from(randomBytes.slice(4, 10)).map(b => b.toString(16).padStart(2, '0')).join('')
  ].join('-')
  
  return uuid
}

const generateUuidValue = (): string => {
  return uuidType.value === 'v7' ? generateUuidV7() : generateUuidV4()
}

const formatUuid = (uuid: string): string => {
  let formatted = uuid
  if (!includeDashes.value) {
    formatted = formatted.replace(/-/g, '')
  }
  return uppercase.value ? formatted.toUpperCase() : formatted
}

const generateUuid = () => {
  const newUuid: Uuid = {
    id: ++uuidCounter,
    value: generateUuidValue(),
    copied: false,
    type: uuidType.value
  }
  uuids.value.unshift(newUuid)
  
  // Auto-copy the newly generated UUID
  copyToClipboard(newUuid, false)
}

const copyToClipboard = async (uuid: Uuid, showFeedback = true) => {
  try {
    await navigator.clipboard.writeText(formatUuid(uuid.value))
    
    if (showFeedback) {
      uuid.copied = true
      setTimeout(() => {
        uuid.copied = false
      }, 2000)
    }
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const copyAllUuids = async () => {
  const allUuids = uuids.value.map(u => formatUuid(u.value)).join('\n')
  try {
    await navigator.clipboard.writeText(allUuids)
    // Show feedback on all items briefly
    uuids.value.forEach(u => u.copied = true)
    setTimeout(() => {
      uuids.value.forEach(u => u.copied = false)
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const removeUuid = (id: number) => {
  uuids.value = uuids.value.filter(u => u.id !== id)
}

const clearAllUuids = () => {
  if (confirm('Are you sure you want to clear all UUIDs?')) {
    uuids.value = []
  }
}
</script>

<style scoped>
.uuid-generator-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto 1.5rem;
}

.uuid-guid-note {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: #edf2f7;
  border-left: 4px solid #667eea;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.95rem;
  color: #2d3748;
  max-width: 600px;
}

.uuid-guid-note svg {
  flex-shrink: 0;
  color: #667eea;
}

.generator-section {
  padding: 2rem 0 4rem;
}

.btn-generate {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-generate:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-generate:active {
  transform: translateY(0);
}

.format-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #4a5568;
  cursor: pointer;
  user-select: none;
}

.format-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.uuid-type-selector {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.type-option {
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  max-width: 280px;
}

.type-radio {
  display: none;
}

.type-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  height: 100%;
}

.type-option:hover .type-card {
  border-color: #667eea;
  background: #f7fafc;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.type-radio:checked + .type-card {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.type-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.type-header strong {
  font-size: 1.1rem;
  color: #1a202c;
}

.type-badge {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-badge-sortable {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.type-card p {
  font-size: 0.9rem;
  color: #718096;
  margin: 0;
}

.uuid-type-indicator {
  display: flex;
  align-items: center;
}

.version-badge {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  min-width: 32px;
  text-align: center;
}

.version-badge.sortable {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.uuids-container {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-height: 300px;
  margin-bottom: 2rem;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #a0aec0;
}

.empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p:first-of-type {
  font-size: 1.1rem;
  font-weight: 600;
  color: #718096;
  margin-bottom: 0.5rem;
}

.uuid-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.uuid-item:hover {
  border-color: #667eea;
  background: #edf2f7;
  transform: translateX(4px);
}

.uuid-item.copied {
  border-color: #48bb78;
  background: #f0fff4;
}

.uuid-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.uuid-value {
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  color: #2d3748;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 6px;
  flex: 1;
}

.copied-badge {
  background: #48bb78;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  animation: fadeIn 0.3s ease;
}

.uuid-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-icon:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-icon.btn-delete:hover {
  border-color: #f56565;
  color: #f56565;
  background: #fff5f5;
}

.bulk-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-bulk {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.2s ease;
}

.btn-bulk:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-bulk.btn-danger:hover {
  border-color: #f56565;
  color: #f56565;
  background: #fff5f5;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
}

.info-card code {
  background: #f7fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #667eea;
  font-size: 0.9em;
}

/* Animations */
.uuid-list-enter-active {
  transition: all 0.3s ease;
}

.uuid-list-leave-active {
  transition: all 0.3s ease;
}

.uuid-list-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.uuid-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .btn-generate {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
  
  .uuid-item {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .uuid-value {
    font-size: 0.85rem;
    word-break: break-all;
  }
  
  .bulk-actions {
    flex-direction: column;
  }
  
  .btn-bulk {
    width: 100%;
    justify-content: center;
  }
}
</style>
